import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AGENDA } from "@/constants/testIds";
import {
  Plus,
  Pencil,
  Trash2,
  CheckSquare,
  Square,
  Save,
  AlertCircle,
  CheckCircle2,
  CalendarCheck,
  ClipboardList,
  BookOpen,
  X,
} from "lucide-react";

const PRIORITY_LABEL = { alta: "Alta", media: "Media", baja: "Baja" };
const STATUS_LABEL = { pendiente: "Pendiente", en_progreso: "En progreso", completada: "Completada" };

const PRI_PILL = {
  alta: "border-red-400 bg-red-50 text-red-700",
  media: "border-amber-400 bg-amber-50 text-amber-700",
  baja: "border-slate-300 bg-slate-50 text-slate-600",
};
const STATUS_PILL = {
  pendiente: "border-amber-400 bg-amber-50 text-amber-700",
  en_progreso: "border-sky-400 bg-sky-50 text-sky-700",
  completada: "border-emerald-400 bg-emerald-50 text-emerald-700",
};

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function MiAgenda() {
  const { user } = useAuth();
  const isAdmin = user?.role === "administrador";
  const [tab, setTab] = useState("tasks");

  return (
    <div data-testid={AGENDA.page} className="p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="font-serif text-3xl text-[#1c1a14] flex items-center gap-2">
          <CalendarCheck className="text-[#c8a04a]" /> Mi Agenda
        </h1>
        <p className="text-[13px] text-[#8b7e5e] mt-1">
          Gestiona tus responsabilidades y registra tu bitácora diaria de actividades.
        </p>
      </div>

      <div className="mb-5 flex gap-2 border-b border-[#d9cfb8]">
        <TabButton
          active={tab === "tasks"}
          onClick={() => setTab("tasks")}
          testId={AGENDA.tabTasks}
          icon={ClipboardList}
        >
          Responsabilidades
        </TabButton>
        <TabButton
          active={tab === "diary"}
          onClick={() => setTab("diary")}
          testId={AGENDA.tabDiary}
          icon={BookOpen}
        >
          Reporte Diario
        </TabButton>
      </div>

      {tab === "tasks" && <TasksPanel isAdmin={isAdmin} currentEmail={user?.email} />}
      {tab === "diary" && <DiaryPanel isAdmin={isAdmin} currentEmail={user?.email} />}
    </div>
  );
}

function TabButton({ active, onClick, children, testId, icon: Icon }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`px-4 py-2 text-[12px] uppercase tracking-widest border-b-2 -mb-px flex items-center gap-2 ${
        active
          ? "border-[#c8a04a] text-[#1c1a14]"
          : "border-transparent text-[#8b7e5e] hover:text-[#1c1a14]"
      }`}
    >
      <Icon size={14} />
      {children}
    </button>
  );
}

// =========== TASKS ===========
function TasksPanel({ isAdmin, currentEmail }) {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState(isAdmin ? "ALL" : "");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (assigneeFilter) params.assigned_to = assigneeFilter;
      const [t, u] = await Promise.all([
        api.get("/agenda/tasks", { params }),
        api.get("/agenda/users"),
      ]);
      setTasks(t.data);
      setUsers(u.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error cargando tareas");
    }
  };

  useEffect(() => {
    load();
  }, [statusFilter, assigneeFilter]);

  const toggleComplete = async (t) => {
    const nextStatus = t.status === "completada" ? "pendiente" : "completada";
    try {
      await api.put(`/agenda/tasks/${t.id}`, { status: nextStatus });
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al actualizar");
    }
  };

  const remove = async (t) => {
    if (!confirm(`¿Eliminar la tarea "${t.title}"?`)) return;
    try {
      await api.delete(`/agenda/tasks/${t.id}`);
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Error eliminando");
    }
  };

  return (
    <div>
      {error && (
        <div className="mb-4 flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[13px] text-red-700">
          <AlertCircle size={14} /> {error}
          <button onClick={() => setError("")} className="ml-auto text-red-500 hover:text-red-700">
            <X size={14} />
          </button>
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-3 items-center justify-between">
        <div className="flex flex-wrap gap-2">
          <select
            data-testid={AGENDA.taskFilter}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-[#d9cfb8] bg-white px-3 py-1.5 text-[13px] rounded"
          >
            <option value="">Todos los estados</option>
            <option value="pendiente">Pendiente</option>
            <option value="en_progreso">En progreso</option>
            <option value="completada">Completada</option>
          </select>
          {isAdmin && (
            <select
              data-testid={AGENDA.taskAssigneeFilter}
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              className="border border-[#d9cfb8] bg-white px-3 py-1.5 text-[13px] rounded"
            >
              <option value="ALL">Todos los usuarios</option>
              {users.map((u) => {
                const label = `${u.name || u.email} (${u.role === "administrador" ? "Admin" : "Secretaria"})`;
                return (
                  <option key={u.email} value={u.email}>{label}</option>
                );
              })}
            </select>
          )}
        </div>
        <button
          data-testid={AGENDA.addTaskButton}
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
          className="inline-flex items-center gap-2 rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a]"
        >
          <Plus size={14} /> Nueva responsabilidad
        </button>
      </div>

      {!tasks.length ? (
        <div className="rounded border border-dashed border-[#d9cfb8] bg-white p-10 text-center text-[#8b7e5e]">
          No hay responsabilidades registradas. Crea una con el botón &quot;Nueva responsabilidad&quot;.
        </div>
      ) : (
        <div data-testid={AGENDA.taskList} className="space-y-2">
          {tasks.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              onToggle={() => toggleComplete(t)}
              onEdit={() => {
                setEditing(t);
                setShowForm(true);
              }}
              onDelete={() => remove(t)}
              isAdmin={isAdmin}
              currentEmail={currentEmail}
            />
          ))}
        </div>
      )}

      {showForm && (
        <TaskForm
          initial={editing}
          users={users}
          isAdmin={isAdmin}
          currentEmail={currentEmail}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function TaskRow({ task, onToggle, onEdit, onDelete }) {
  const done = task.status === "completada";
  return (
    <div
      data-testid={AGENDA.taskRow(task.id)}
      className={`rounded border bg-white px-4 py-3 flex items-start gap-3 ${
        done ? "border-emerald-200 bg-emerald-50/40" : "border-[#d9cfb8]"
      }`}
    >
      <button
        data-testid={AGENDA.taskCheckbox(task.id)}
        onClick={onToggle}
        className="mt-1 text-[#5d5849] hover:text-[#1c1a14]"
        title={done ? "Marcar como pendiente" : "Marcar como completada"}
      >
        {done ? (
          <CheckSquare size={18} className="text-emerald-600" />
        ) : (
          <Square size={18} />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className={`font-medium ${done ? "line-through text-[#8b7e5e]" : "text-[#1c1a14]"}`}>
            {task.title}
          </div>
          <span className={`text-[9px] uppercase tracking-widest border rounded px-1.5 py-0.5 ${PRI_PILL[task.priority]}`}>
            {PRIORITY_LABEL[task.priority]}
          </span>
          <span className={`text-[9px] uppercase tracking-widest border rounded px-1.5 py-0.5 ${STATUS_PILL[task.status]}`}>
            {STATUS_LABEL[task.status]}
          </span>
        </div>
        {task.description && (
          <p className="text-[12px] text-[#5d5849] mt-1 whitespace-pre-wrap">{task.description}</p>
        )}
        <div className="text-[11px] text-[#8b7e5e] mt-1">
          {task.due_date && <span>Vence: {task.due_date} · </span>}
          <span>Asignada a: {task.assigned_to}</span>
        </div>
      </div>
      <div className="flex gap-1">
        <button
          data-testid={AGENDA.taskEditButton(task.id)}
          onClick={onEdit}
          className="text-[#5d5849] hover:text-[#1c1a14] p-1"
          title="Editar"
        >
          <Pencil size={14} />
        </button>
        <button
          data-testid={AGENDA.taskDeleteButton(task.id)}
          onClick={onDelete}
          className="text-red-500 hover:text-red-700 p-1"
          title="Eliminar"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

function TaskForm({ initial, users, isAdmin, currentEmail, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial || {
      title: "",
      description: "",
      priority: "media",
      due_date: "",
      assigned_to: currentEmail || "",
      status: "pendiente",
    },
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (initial) {
        await api.put(`/agenda/tasks/${initial.id}`, form);
      } else {
        await api.post("/agenda/tasks", form);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={initial ? "Editar responsabilidad" : "Nueva responsabilidad"} onClose={onClose} testId={AGENDA.taskModal}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Título *">
          <input
            data-testid={AGENDA.taskTitleInput}
            value={form.title}
            onChange={(e) => update("title", e.target.value)}
            className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
            required
          />
        </Field>
        <Field label="Descripción">
          <textarea
            data-testid={AGENDA.taskDescInput}
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            rows={3}
            className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Prioridad">
            <select
              data-testid={AGENDA.taskPrioritySelect}
              value={form.priority}
              onChange={(e) => update("priority", e.target.value)}
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
            >
              <option value="alta">Alta</option>
              <option value="media">Media</option>
              <option value="baja">Baja</option>
            </select>
          </Field>
          <Field label="Fecha de vencimiento">
            <input
              data-testid={AGENDA.taskDueInput}
              type="date"
              value={form.due_date || ""}
              onChange={(e) => update("due_date", e.target.value)}
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
            />
          </Field>
          <Field label="Asignada a">
            <select
              data-testid={AGENDA.taskAssignSelect}
              value={form.assigned_to}
              onChange={(e) => update("assigned_to", e.target.value)}
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
              disabled={!isAdmin}
            >
              {isAdmin
                ? users.map((u) => (
                    <option key={u.email} value={u.email}>
                      {u.name || u.email}
                    </option>
                  ))
                : (
                    <option value={currentEmail}>{currentEmail} (yo)</option>
                  )}
            </select>
          </Field>
          <Field label="Estado">
            <select
              data-testid={AGENDA.taskStatusSelect}
              value={form.status}
              onChange={(e) => update("status", e.target.value)}
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
            >
              <option value="pendiente">Pendiente</option>
              <option value="en_progreso">En progreso</option>
              <option value="completada">Completada</option>
            </select>
          </Field>
        </div>

        {err && (
          <div className="flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700">
            <AlertCircle size={14} /> {err}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            data-testid={AGENDA.taskCancelButton}
            onClick={onClose}
            disabled={busy}
            className="rounded border border-[#d9cfb8] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]"
          >
            Cancelar
          </button>
          <button
            type="submit"
            data-testid={AGENDA.taskSaveButton}
            disabled={busy}
            className="rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a] disabled:opacity-50"
          >
            {busy ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// =========== DIARY ===========
function DiaryPanel({ isAdmin, currentEmail }) {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(currentEmail);
  const [date, setDate] = useState(todayStr());
  const [logEntry, setLogEntry] = useState("");
  const [tasksCompleted, setTasksCompleted] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");
  const [error, setError] = useState("");

  const viewingOwn = selectedUser === currentEmail || !selectedUser;

  const loadUsers = async () => {
    try {
      const u = await api.get("/agenda/users");
      setUsers(u.data);
    } catch (e) {
      // Non-blocking: agenda still works without the assignable-users list.
      console.warn("MiAgenda.loadUsers failed", e);
    }
  };

  const loadDay = async () => {
    setError("");
    try {
      if (viewingOwn && date === todayStr()) {
        // own + today: use /today endpoint for upsert
        const r = await api.get("/agenda/daily-reports/today");
        setLogEntry(r.data.log_entry || "");
        setTasksCompleted(r.data.tasks_completed || []);
      } else {
        // historical or another user's day
        const params = { date_from: date, date_to: date };
        if (isAdmin && !viewingOwn) params.user_email = selectedUser;
        const r = await api.get("/agenda/daily-reports", { params });
        if (r.data.length) {
          setLogEntry(r.data[0].log_entry || "");
          setTasksCompleted(r.data[0].tasks_completed || []);
        } else {
          setLogEntry("");
          setTasksCompleted([]);
        }
      }

      // load my tasks for today (only for own bitácora)
      if (viewingOwn) {
        const t = await api.get("/agenda/tasks");
        setTodayTasks(t.data);
      } else {
        const t = await api.get("/agenda/tasks", { params: { assigned_to: selectedUser } });
        setTodayTasks(t.data);
      }

      // history (last 30 entries)
      const params = {};
      if (isAdmin && !viewingOwn) params.user_email = selectedUser;
      const h = await api.get("/agenda/daily-reports", { params });
      setHistory(h.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error cargando reporte");
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadDay();
    })();
    return () => { cancelled = true; };
  }, [selectedUser, date]);

  const toggleTask = (id) => {
    setTasksCompleted((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const save = async () => {
    if (!viewingOwn) return;
    setBusy(true);
    setError("");
    try {
      await api.post("/agenda/daily-reports", {
        date,
        log_entry: logEntry,
        tasks_completed: tasksCompleted,
      });
      setFlash("Reporte guardado correctamente");
      setTimeout(() => setFlash(""), 2500);
      loadDay();
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <div className="rounded border border-[#d9cfb8] bg-white p-5">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#8b7e5e]">Fecha</label>
              <input
                data-testid={AGENDA.diaryDateInput}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                max={todayStr()}
                className="border border-[#d9cfb8] bg-white px-3 py-1.5 text-[13px] rounded"
              />
            </div>
            {isAdmin && (
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#8b7e5e]">Usuario</label>
                <select
                  data-testid={AGENDA.diaryUserSelect}
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="border border-[#d9cfb8] bg-white px-3 py-1.5 text-[13px] rounded"
                >
                  {users.map((u) => {
                    const label = `${u.name || u.email} (${u.role === "administrador" ? "Admin" : "Secretaria"})`;
                    return (
                      <option key={u.email} value={u.email}>{label}</option>
                    );
                  })}
                </select>
              </div>
            )}
          </div>

          {flash && (
            <div className="mb-3 flex items-center gap-2 rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-[13px] text-emerald-700">
              <CheckCircle2 size={14} /> {flash}
            </div>
          )}
          {error && (
            <div className="mb-3 flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[13px] text-red-700">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
              Bitácora del día
            </label>
            <textarea
              data-testid={AGENDA.diaryLogTextarea}
              value={logEntry}
              onChange={(e) => setLogEntry(e.target.value)}
              rows={8}
              disabled={!viewingOwn}
              placeholder="Describe las actividades realizadas hoy..."
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded disabled:bg-[#fbf7ee] disabled:text-[#5d5849]"
            />
          </div>

          <div className="mb-4">
            <label className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-2">
              Tareas completadas hoy
            </label>
            {todayTasks.length === 0 ? (
              <p className="text-[12px] text-[#8b7e5e] italic">
                No hay responsabilidades registradas para este usuario.
              </p>
            ) : (
              <div className="space-y-1 max-h-60 overflow-y-auto border border-[#ebe2c9] rounded p-2 bg-[#fbf7ee]">
                {todayTasks.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-[13px] py-1">
                    <input
                      type="checkbox"
                      checked={tasksCompleted.includes(t.id)}
                      disabled={!viewingOwn}
                      onChange={() => toggleTask(t.id)}
                      className="accent-[#c8a04a]"
                    />
                    <span className={tasksCompleted.includes(t.id) ? "line-through text-[#8b7e5e]" : ""}>
                      {t.title}
                    </span>
                    <span className={`text-[9px] uppercase tracking-widest border rounded px-1 ${STATUS_PILL[t.status]}`}>
                      {STATUS_LABEL[t.status]}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {viewingOwn && (
            <div className="flex justify-end">
              <button
                data-testid={AGENDA.diarySaveButton}
                onClick={save}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a] disabled:opacity-50"
              >
                <Save size={14} />
                {busy ? "Guardando..." : "Guardar reporte"}
              </button>
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="rounded border border-[#d9cfb8] bg-white p-4">
          <h3 className="text-[11px] uppercase tracking-widest text-[#8b7e5e] mb-3">
            Historial de reportes
          </h3>
          <div data-testid={AGENDA.diaryHistory} className="space-y-2 max-h-[600px] overflow-y-auto">
            {history.length === 0 && (
              <p className="text-[12px] text-[#8b7e5e] italic">Sin reportes aún.</p>
            )}
            {history.map((h) => (
              <button
                key={h.id}
                data-testid={AGENDA.diaryEntry(h.id)}
                onClick={() => setDate(h.date)}
                className={`w-full text-left rounded border px-3 py-2 hover:bg-[#fbf7ee] ${
                  h.date === date ? "border-[#c8a04a] bg-[#fbf7ee]" : "border-[#ebe2c9] bg-white"
                }`}
              >
                <div className="text-[11px] font-medium text-[#1c1a14]">{h.date}</div>
                <div className="text-[10px] text-[#8b7e5e] truncate">
                  {(h.log_entry || "Sin bitácora").slice(0, 60)}
                </div>
                <div className="text-[10px] text-[#8b7e5e]">
                  {(h.tasks_completed_titles || h.tasks_completed || []).length} tarea(s) completadas
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">{label}</span>
      {children}
    </label>
  );
}

function Modal({ title, onClose, testId, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        data-testid={testId}
        className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded border border-[#d9cfb8] bg-white p-6 shadow-xl"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg text-[#1c1a14]">{title}</h3>
          <button onClick={onClose} className="text-[#8b7e5e] hover:text-[#1c1a14]">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
